import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, throwError, Observable, Subject } from 'rxjs';
import { tap, shareReplay, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { MatchingPeriod } from '../interfaces/matchingPeriod';
import { Pair } from '../interfaces/pair';

// Error name used when the backend rejects a matching period name (invalid format or already taken)
export const INVALID_MATCHING_PERIOD_NAME = 'InvalidMatchingPeriodName';

@Injectable({
  providedIn: 'root'
})
export class MatchingPeriodService {
  private apiUrl = environment.apiUrl + 'matchingPeriods';

  // Subject to notify when a new matching period is created
  private matchingPeriodCreated$ = new Subject<MatchingPeriod>();

  // Observable that components can subscribe to
  onMatchingPeriodCreated = this.matchingPeriodCreated$.asObservable();

  // Subject to notify when a matchrun is completed
  private matchrunCompleted$ = new Subject<void>();

  // Observable that components can subscribe to
  onMatchrunCompleted = this.matchrunCompleted$.asObservable();

  // PHASE 4 OPTIMIZATION: Cache for latest matching period to avoid redundant API calls
  // Multiple components (dashboard, menu, etc.) call getLatestMatchingPeriod() simultaneously
  // shareReplay ensures only 1 HTTP request is made and the result is shared with all subscribers
  private latestMatchingPeriodCache$?: Observable<MatchingPeriod | null>;

  constructor(private http: HttpClient) {}

  createMatchingPeriod(matchingPeriodName?: string): Observable<MatchingPeriod> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const body = matchingPeriodName ? { matchingPeriodName } : null;

    return this.http.post<MatchingPeriod>(this.apiUrl, body, { headers }).pipe(
      tap((matchingPeriod) => {
        // PHASE 4 OPTIMIZATION: Clear cache when new matching period is created
        this.latestMatchingPeriodCache$ = undefined;

        // Emit event when matching period is created
        this.matchingPeriodCreated$.next(matchingPeriod);
      }),
      catchError((error) => {
        console.error('Error creating matchingPeriod:', error);
        // Keep the backend message for a rejected name (invalid format or already existing) so the dialog can show it
        if (error?.status === 400 && error?.error?.error) {
          const nameError = new Error(error.error.error);
          nameError.name = INVALID_MATCHING_PERIOD_NAME;
          return throwError(() => nameError);
        }
        return throwError(() => new Error('Failed to create matchingPeriod'));
      })
    );
  }

  // Name proposed in the new matching period dialog (next whole quarter)
  getNextMatchingPeriodName(): Observable<string> {
    return this.http.get<{ matchingPeriodName: string }>(`${this.apiUrl}/next-name`).pipe(
      map((response) => response.matchingPeriodName),
      catchError((error) => {
        console.error('Error getting next matchingPeriod name:', error);
        return throwError(() => new Error('Failed to get next matchingPeriod name'));
      })
    );
  }

  getLatestMatchingPeriod(): Observable<MatchingPeriod | null> {
    // PHASE 4 OPTIMIZATION: Use cached observable to prevent redundant API calls
    // If cache doesn't exist, create it with shareReplay to share the result among all subscribers
    if (!this.latestMatchingPeriodCache$) {
      this.latestMatchingPeriodCache$ = this.http.get<MatchingPeriod | null>(this.apiUrl + '/latest').pipe(
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }
    return this.latestMatchingPeriodCache$;
  }

  getLatestMatchingPeriodForHLALab(): Observable<MatchingPeriod | null> {
    return this.http.get<MatchingPeriod | null>(this.apiUrl + '/latest-for-hla-lab').pipe(
      catchError((error) => {
        console.error('Error getting latest matching period for HLA Lab:', error);
        return throwError(() => new Error('Failed to get latest matching period for HLA Lab'));
      })
    );
  }

  getAllMatchingPeriods(): Observable<MatchingPeriod[]> {
    return this.http.get<MatchingPeriod[]>(this.apiUrl);
  }

  // Method to add pairs to a matchingPeriod
  addPairsToMatchingPeriod(pairs: Pair[]): Observable<MatchingPeriod> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post<MatchingPeriod>(`${this.apiUrl}/pairs`, pairs, { headers }).pipe(
      tap(() => {
        // PHASE 4 OPTIMIZATION: Clear cache when pairs are added (may affect latest matching period)
        this.latestMatchingPeriodCache$ = undefined;
      }),
      catchError((error) => {
        console.error('Error adding pairs to matchingPeriod:', error);
        return throwError(() => new Error('Failed to create matchingPeriod'));
      })
    );
  }

  activatePair(pairId: string): Observable<Pair> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.patch<Pair>(`${this.apiUrl}/pairstatus/${pairId}`, { headers }).pipe(
      catchError((error) => {
        console.error('Error changing status of pair:', error);
        return throwError(() => new Error('Failed to change status of pair'));
      })
    );
  }

  deactivatePair(pair: Pair): Observable<Pair> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.patch<Pair>(`${this.apiUrl}/pairstatus/${pair.id}`, pair, { headers }).pipe(
      catchError((error) => {
        console.error('Error changing status of pair:', error);
        return throwError(() => new Error('Failed to change status of pair'));
      })
    );
  }

  getMatchingPeriod(matchingPeriodName: string): Observable<MatchingPeriod> {
    return this.http.get<MatchingPeriod>(`${this.apiUrl}/${matchingPeriodName}`).pipe(
      catchError((error) => {
        console.error('Error getting matchingperiods:', error);
        return throwError(() => new Error('Failed to get matchingperiods'));
      })
    );
  }

  // Get participants for a specific matchrun (with CENTER role filtering)
  getMatchRunParticipants(matchingPeriodName: string, matchRunId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/${matchingPeriodName}/matchruns/${matchRunId}/participants`).pipe(
      catchError((error) => {
        console.error('Error getting matchrun participants:', error);
        return throwError(() => new Error('Failed to get matchrun participants'));
      })
    );
  }

  /**
   * Get pre-assembled chain data for a specific matchrun
   * OPTIMIZATION: Returns all chains with participants, crossmatches, and chain statuses in a single response
   * Replaces multiple API calls (N chains × [crossmatches + chain status]) with a single call
   */
  getMatchRunChains(matchingPeriodName: string, matchRunId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/${matchingPeriodName}/matchruns/${matchRunId}/chains`).pipe(
      catchError((error) => {
        console.error('Error getting matchrun chains:', error);
        return throwError(() => new Error('Failed to get matchrun chains'));
      })
    );
  }

  /**
   * Invalidates the cached latest matching period data.
   * Call this when a matchrun is created to ensure fresh data is fetched.
   */
  clearLatestMatchingPeriodCache(): void {
    this.latestMatchingPeriodCache$ = undefined;
  }

  /**
   * Notifies subscribers that a matchrun has been completed.
   * Call this when a matchrun finishes processing to trigger UI updates.
   */
  notifyMatchrunCompleted(): void {
    this.latestMatchingPeriodCache$ = undefined;
    this.matchrunCompleted$.next();
  }
}
